import * as React from 'react';
import { Box } from '@mui/system';
import { Button, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AgentChat } from 'bloggers-backend/lib/aiagents/domain';
import { FormatUserTime, useUserTime } from '../../../hooks/useUserTime';

interface ChatSidebarProps {
  chats: AgentChat[];
  currentChatId?: string;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
}

// Each row is labelled by the chat title (the first user message, captured at creation),
// falling back to the creation timestamp.
function chatLabel(chat: AgentChat, formatUserTime: FormatUserTime): string {
  return chat.title?.trim() || formatUserTime(chat.createdAt) || 'New chat';
}

export default function ChatSidebar({ chats, currentChatId, onSelectChat, onNewChat }: ChatSidebarProps) {
  const formatUserTime = useUserTime();

  return (
    <Box sx={{ width: 280, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ p: 1 }}>
        <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={onNewChat}>
          New chat
        </Button>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {chats.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ mt: 4, px: 2 }}>
            No chats yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {chats.map((chat) => (
              <ListItemButton
                key={chat.id}
                selected={chat.id === currentChatId}
                onClick={() => onSelectChat(chat.id)}
              >
                <ListItemText
                  primary={chatLabel(chat, formatUserTime)}
                  secondary={formatUserTime(chat.createdAt)}
                  primaryTypographyProps={{
                    noWrap: true,
                    sx: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
                  }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
}
